import type { PointCodeValidationResponse } from '~/types/api/client/pointshop'
import type { CustomerDetailsResponse } from '~~/server/api/client/webusers/customerdetails.get'
import type { PointTransactionInfo } from '~~/server/api/client/webusers/pointhistory.get'

export interface PointCodeCustomerSummary {
  customer: CustomerDetailsResponse
  points: PointTransactionInfo[]
  currentBalance: number
  totalTransactions: number
  earnedCount: number
  redeemedCount: number
  recentTransactions: PointTransactionInfo[]
}

export function usePointCodeValidation(clientId: MaybeRefOrGetter<number | null>) {
  const pinValue2 = ref<string[]>([])
  const pinValue3 = ref<string[]>([])
  const pinValue4 = ref<string[]>([])

  const loading = ref(false)
  const validationResult = ref<PointCodeValidationResponse | null>(null)
  const error = ref<string | null>(null)
  const hasValidated = ref(false)

  const customerSummary = ref<PointCodeCustomerSummary | null>(null)
  const customerSummaryLoading = ref(false)

  const fullCode = computed(() => {
    if (!isComplete.value) return ''
    return `${pinValue2.value.join('')}-${pinValue3.value.join('')}-${pinValue4.value.join('')}`
  })

  const isComplete = computed(() =>
    [pinValue2, pinValue3, pinValue4].every(group =>
      group.value.length === 3 && group.value.every(char => char.length > 0)
    )
  )

  const inputColor = computed(() => {
    if (!hasValidated.value) return 'primary'
    if (validationResult.value?.status === 'Valid') return 'success'
    if (validationResult.value?.status === 'Invalid') return 'error'
    if (validationResult.value?.status === 'Duplicated') return 'warning'
    return 'primary'
  })

  const primaryUsage = computed(() => validationResult.value?.existingPointTrxInfo?.[0] ?? null)

  const resetForm = () => {
    pinValue2.value = []
    pinValue3.value = []
    pinValue4.value = []
    validationResult.value = null
    error.value = null
    hasValidated.value = false
    customerSummary.value = null
  }

  const validateCode = async () => {
    const id = toValue(clientId)
    if (!isComplete.value || !id) return

    loading.value = true
    error.value = null
    validationResult.value = null
    hasValidated.value = false
    customerSummary.value = null

    try {
      const result = await $fetch<PointCodeValidationResponse>(
        '/api/client/webusers/pointshop/pointcode-validator',
        {
          query: { pointCode: fullCode.value, clientId: id },
          headers: useRequestHeaders(['cookie']) as HeadersInit,
        }
      )
      validationResult.value = result
      hasValidated.value = true
      await loadCustomerSummary(id)
    } catch (err: unknown) {
      const fetchError = err as { data?: PointCodeValidationResponse; message?: string; statusCode?: number }
      if (fetchError.data && 'status' in fetchError.data) {
        validationResult.value = fetchError.data
        hasValidated.value = true
        const id = toValue(clientId)
        if (id) await loadCustomerSummary(id)
      } else {
        error.value = fetchError.message || 'Failed to validate point code'
        hasValidated.value = true
      }
    } finally {
      loading.value = false
    }
  }

  const loadCustomerSummary = async (id: number) => {
    const usage = primaryUsage.value
    if (!usage?.webUserId) {
      customerSummary.value = null
      return
    }

    customerSummaryLoading.value = true
    try {
      const [customer, points] = await Promise.all([
        $fetch<CustomerDetailsResponse>('/api/client/webusers/customerdetails', {
          query: { wuId: usage.webUserId, clientId: id },
          headers: useRequestHeaders(['cookie']) as HeadersInit,
        }),
        $fetch<PointTransactionInfo[]>('/api/client/webusers/pointhistory', {
          query: { wuId: usage.webUserId, clientId: id },
          headers: useRequestHeaders(['cookie']) as HeadersInit,
        }),
      ])

      const currentBalance = points[0]?.totalPointBalance ?? points[0]?.balance ?? 0

      customerSummary.value = {
        customer,
        points,
        currentBalance,
        totalTransactions: points.length,
        earnedCount: points.filter(point => point.points > 0).length,
        redeemedCount: points.filter(point => point.points < 0).length,
        recentTransactions: points.slice(0, 5),
      }
    } catch {
      customerSummary.value = null
    } finally {
      customerSummaryLoading.value = false
    }
  }

  const applyPastedCode = (pastedText: string) => {
    const cleanedText = pastedText.replace(/-/g, '').toUpperCase().slice(0, 9)
    if (!cleanedText.length) return

    pinValue2.value = cleanedText.slice(0, 3).split('')
    pinValue3.value = cleanedText.slice(3, 6).split('')
    pinValue4.value = cleanedText.slice(6, 9).split('')
  }

  const suggestedReportReason = computed(() => {
    if (!validationResult.value) return undefined
    if (validationResult.value.status === 'Duplicated') return 'ALRU'
    if (validationResult.value.status === 'Invalid') return 'INVC'
    return undefined
  })

  return {
    pinValue2,
    pinValue3,
    pinValue4,
    loading,
    validationResult,
    error,
    hasValidated,
    fullCode,
    isComplete,
    inputColor,
    primaryUsage,
    customerSummary,
    customerSummaryLoading,
    suggestedReportReason,
    validateCode,
    resetForm,
    applyPastedCode,
  }
}
